/**
 * Individual calculator components used inside the Toolbox.
 *
 * All maths runs client-side — these are quick reference tools, not sales
 * lines, so they don't need a backend round-trip.
 *
 * Coefficients supplied by the user (Feb 2026):
 *   MOT Type 1        — 100×100×150mm ≡ 3,150 t   → 2.10 t/m³
 *   Base Asphalt      — 100m² @60mm  ≡ 15.5 t     → 2.583 t/m³
 *   Porous Concrete   — 10×10×100mm  ≡ 10 m³      → volume-only, no density
 */
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------
function useAreaAndDepth() {
  const [area, setArea] = useState('');
  const [depth, setDepth] = useState('');
  const a = parseFloat(area) || 0;
  const d = parseFloat(depth) || 0;
  const cubicMetres = a * (d / 1000); // depth is in mm
  return { area, setArea, depth, setDepth, a, d, cubicMetres };
}

function CalcShell({ title, subtitle, children }) {
  return (
    <div>
      {subtitle && <p className="text-sm text-slate-500 mb-3">{subtitle}</p>}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">{children}</div>
    </div>
  );
}

function ResultTile({ label, value, unit, tone = 'blue', testid }) {
  const tones = {
    blue: 'bg-[#2F5E96]/10 border-[#2F5E96]/30 text-[#2F5E96]',
    purple: 'bg-[#7A0B7A]/10 border-[#7A0B7A]/30 text-[#7A0B7A]',
    amber: 'bg-amber-100 border-amber-300 text-amber-700',
    emerald: 'bg-emerald-100 border-emerald-300 text-emerald-700',
  };
  return (
    <div className={`rounded-lg border-2 p-4 text-center ${tones[tone]}`} data-testid={testid}>
      <p className="text-[11px] font-bold uppercase tracking-wider opacity-80">{label}</p>
      <p className="text-3xl font-bold mt-1">{value}</p>
      <p className="text-xs mt-1 opacity-80">{unit}</p>
    </div>
  );
}

const AreaDepthInputs = ({ a, d, setArea, setDepth, areaLabel = 'Area (m²)', depthLabel = 'Depth (mm)' }) => (
  <>
    <div>
      <Label>{areaLabel}</Label>
      <Input type="number" step="0.1" min="0" value={a === 0 ? '' : a} onChange={(e) => setArea(e.target.value)}
        placeholder="e.g. 100" data-testid="calc-area" />
    </div>
    <div>
      <Label>{depthLabel}</Label>
      <Input type="number" step="1" min="0" value={d === 0 ? '' : d} onChange={(e) => setDepth(e.target.value)}
        placeholder="e.g. 150" data-testid="calc-depth" />
    </div>
  </>
);

// ---------------------------------------------------------------------------
// MOT Type 1
// ---------------------------------------------------------------------------
export function MotType1Calc() {
  const { area, setArea, depth, setDepth, a, d, cubicMetres } = useAreaAndDepth();
  const DENSITY = 2.1; // t/m³
  const tonnes = cubicMetres * DENSITY;

  return (
    <CalcShell subtitle="Compacted MOT Type 1 sub-base. Density 2.10 t/m³ (100m×100m×150mm ≡ 3,150 t).">
      <AreaDepthInputs a={a} d={d} setArea={setArea} setDepth={setDepth} />
      <ResultTile label="Volume needed" value={cubicMetres.toFixed(2)} unit="m³" tone="blue" testid="mot-volume" />
      <ResultTile label="Tonnes required" value={tonnes.toFixed(2)} unit={`tonnes @ ${DENSITY} t/m³`} tone="amber" testid="mot-tonnes" />
    </CalcShell>
  );
}

// ---------------------------------------------------------------------------
// Base Asphalt (aka Tarmac base course)
// ---------------------------------------------------------------------------
export function BaseAsphaltCalc() {
  const { area, setArea, depth, setDepth, a, d, cubicMetres } = useAreaAndDepth();
  const DENSITY = 2.583; // t/m³  — 100m² @60mm ≡ 15.5 t (user-supplied)
  const tonnes = cubicMetres * DENSITY;

  return (
    <CalcShell subtitle="Base course asphalt. Density 2.583 t/m³ (100m² @60mm ≡ 15.5 t).">
      <AreaDepthInputs a={a} d={d} setArea={setArea} setDepth={setDepth} />
      <ResultTile label="Volume needed" value={cubicMetres.toFixed(2)} unit="m³" tone="blue" testid="asphalt-volume" />
      <ResultTile label="Tonnes required" value={tonnes.toFixed(2)} unit={`tonnes @ ${DENSITY} t/m³`} tone="amber" testid="asphalt-tonnes" />
    </CalcShell>
  );
}

// ---------------------------------------------------------------------------
// Porous Concrete — volume-only calc (order in m³, not tonnes)
// ---------------------------------------------------------------------------
export function PorousConcreteCalc() {
  const { area, setArea, depth, setDepth, a, d, cubicMetres } = useAreaAndDepth();
  return (
    <CalcShell subtitle="Ready-mixed porous concrete is ordered by volume (10m×10m×100mm ≡ 10 m³).">
      <AreaDepthInputs a={a} d={d} setArea={setArea} setDepth={setDepth} />
      <ResultTile label="Concrete needed" value={cubicMetres.toFixed(2)} unit="m³ (cubic metres)" tone="emerald" testid="porous-volume" />
      <div className="rounded-lg border-2 border-dashed border-slate-300 p-4 text-center text-xs text-slate-500">
        Tip: order a small 5–10% overage for spillage and off-cuts on tricky perimeters.
      </div>
    </CalcShell>
  );
}

// ---------------------------------------------------------------------------
// Block Paving edging — how many blocks around the perimeter?
// ---------------------------------------------------------------------------
// User asked for: enter perimeter in linear metres + block length in mm →
// return the number of blocks needed for a single-course edging run.
// Blocks = perimeter (m) × 1000 ÷ block length (mm), rounded up.
// A tiny "spare" hint at the bottom nudges installers to add ~5% for cuts.
// Full-area mode: blocks = area (m²) ÷ (length × width in m²), rounded up,
// plus the standard 5% cuts allowance. Default block 200×100mm ≈ 50/m².
const BLOCK_MODES = [
  { key: 'perimeter', label: 'Edging (perimeter)' },
  { key: 'area', label: 'Full area' },
];

function BlockModeToggle({ mode, setMode }) {
  return (
    <div className="md:col-span-2 inline-flex rounded-lg border border-slate-200 bg-slate-50 p-1 gap-1" role="tablist" data-testid="block-mode-toggle">
      {BLOCK_MODES.map((m) => (
        <button
          key={m.key}
          type="button"
          role="tab"
          aria-selected={mode === m.key}
          onClick={() => setMode(m.key)}
          data-testid={`block-mode-${m.key}`}
          className={`px-3 py-1.5 rounded-md text-xs font-bold transition-colors ${mode === m.key ? 'bg-[#2F5E96] text-white shadow-sm' : 'text-slate-600 hover:bg-white'}`}
        >
          {m.label}
        </button>
      ))}
    </div>
  );
}

function BlockPerimeterMode({ blockLen, setBlockLen }) {
  const [perimeter, setPerimeter] = useState('');
  const p = parseFloat(perimeter) || 0;
  const b = parseFloat(blockLen) || 0;
  const raw = b > 0 ? (p * 1000) / b : 0;
  return (
    <>
      <div>
        <Label>Perimeter length (m)</Label>
        <Input type="number" step="0.1" min="0" value={perimeter} onChange={(e) => setPerimeter(e.target.value)}
          placeholder="e.g. 42.5" data-testid="block-perimeter" />
      </div>
      <div>
        <Label>Block length (mm)</Label>
        <Input type="number" step="1" min="1" value={blockLen} onChange={(e) => setBlockLen(e.target.value)}
          placeholder="e.g. 200" data-testid="block-length" />
      </div>
      <ResultTile label="Blocks needed" value={Math.ceil(raw) || 0} unit="blocks (exact)" tone="blue" testid="block-exact" />
      <ResultTile label="With 5% for cuts" value={Math.ceil(raw * 1.05) || 0} unit="blocks (recommended order qty)" tone="amber" testid="block-with-waste" />
    </>
  );
}

function BlockAreaMode({ blockLen, setBlockLen }) {
  const [area, setArea] = useState('');
  const [blockWid, setBlockWid] = useState('100');
  const a = parseFloat(area) || 0;
  const l = parseFloat(blockLen) || 0;
  const w = parseFloat(blockWid) || 0;
  const blockM2 = (l / 1000) * (w / 1000);
  const perM2 = blockM2 > 0 ? 1 / blockM2 : 0;
  const raw = blockM2 > 0 ? a / blockM2 : 0;
  return (
    <>
      <div className="md:col-span-2">
        <Label>Area to pave (m²)</Label>
        <Input type="number" step="0.1" min="0" value={area} onChange={(e) => setArea(e.target.value)}
          placeholder="e.g. 60" data-testid="block-area" />
      </div>
      <div>
        <Label>Block length (mm)</Label>
        <Input type="number" step="1" min="1" value={blockLen} onChange={(e) => setBlockLen(e.target.value)}
          placeholder="e.g. 200" data-testid="block-length" />
      </div>
      <div>
        <Label>Block width (mm)</Label>
        <Input type="number" step="1" min="1" value={blockWid} onChange={(e) => setBlockWid(e.target.value)}
          placeholder="e.g. 100" data-testid="block-width" />
      </div>
      <ResultTile label="Blocks needed" value={Math.ceil(raw) || 0} unit={`blocks (exact) · ${perM2 ? perM2.toFixed(1) : '—'} per m²`} tone="blue" testid="block-area-exact" />
      <ResultTile label="With 5% for cuts" value={Math.ceil(raw * 1.05) || 0} unit="blocks (recommended order qty)" tone="amber" testid="block-area-with-waste" />
    </>
  );
}

export function BlockPavingCalc() {
  const [mode, setMode] = useState('perimeter');
  const [blockLen, setBlockLen] = useState('200');
  const subtitle = mode === 'area'
    ? 'Blocks needed to cover a full area — enter the m² plus block dimensions.'
    : 'Blocks needed for a single-course perimeter edging run.';

  return (
    <CalcShell subtitle={subtitle}>
      <BlockModeToggle mode={mode} setMode={setMode} />
      {mode === 'area'
        ? <BlockAreaMode blockLen={blockLen} setBlockLen={setBlockLen} />
        : <BlockPerimeterMode blockLen={blockLen} setBlockLen={setBlockLen} />}
    </CalcShell>
  );
}

// ---------------------------------------------------------------------------
// Self-Smoothing Compound (ProEpoxy prep)
// ---------------------------------------------------------------------------
// 1 bag = 5 m² @ 3mm  ⟺  2.5 m² @ 6mm.
// Both equate to 15 m²·mm per bag, so `bags = ceil(area × depth ÷ 15)`.
export function SelfSmoothingCalc() {
  const { area, setArea, depth, setDepth, a, d } = useAreaAndDepth();
  const BAG_YIELD = 15; // m²·mm per bag
  const rawBags = (a * d) / BAG_YIELD;
  const bags = Math.ceil(rawBags);
  const bagsWithWaste = Math.ceil(rawBags * 1.05);

  return (
    <CalcShell subtitle="1 bag covers 5 m² @ 3mm or 2.5 m² @ 6mm (15 m²·mm per bag).">
      <AreaDepthInputs a={a} d={d} setArea={setArea} setDepth={setDepth} />
      <ResultTile label="Bags needed" value={bags || 0} unit="bags (exact)" tone="purple" testid="ss-exact" />
      <ResultTile label="With 5% for waste" value={bagsWithWaste || 0} unit="bags (recommended order qty)" tone="amber" testid="ss-with-waste" />
    </CalcShell>
  );
}

// ---------------------------------------------------------------------------
// Ventilation Calculator (ProEpoxy)
// ---------------------------------------------------------------------------
// Sizes an extractor for a resin/epoxy install:
//   volume (m³)      = floor area × ceiling height
//   required rate    = volume × air changes per hour (m³/h)
// Recommended minimum for resin is 3 ACH — installers can dial it up when
// the room's small or the resin's solvent-heavy.
export function VentilationCalc() {
  const [area, setArea] = useState('');
  const [height, setHeight] = useState('2.4');
  const [ach, setAch] = useState('3');
  const a = parseFloat(area) || 0;
  const h = parseFloat(height) || 0;
  const n = parseFloat(ach) || 0;
  const volume = a * h;
  const rate = volume * n;
  const cfm = rate * 0.5886; // m³/h → CFM (1 m³/h ≈ 0.5886 CFM)

  return (
    <CalcShell subtitle="Volume × air changes per hour = the extractor rate you need. 3 ACH is the recommended minimum for resin.">
      <div>
        <Label>Floor area (m²)</Label>
        <Input
          type="number" step="0.1" min="0"
          value={area} onChange={(e) => setArea(e.target.value)}
          placeholder="e.g. 40" data-testid="vent-area"
        />
      </div>
      <div>
        <Label>Ceiling height (m)</Label>
        <Input
          type="number" step="0.1" min="0"
          value={height} onChange={(e) => setHeight(e.target.value)}
          placeholder="e.g. 2.4" data-testid="vent-height"
        />
      </div>
      <div className="md:col-span-2">
        <Label>Air changes per hour (ACH)</Label>
        <Input
          type="number" step="0.5" min="0"
          value={ach} onChange={(e) => setAch(e.target.value)}
          placeholder="3 recommended minimum" data-testid="vent-ach"
        />
        <p className="text-[11px] text-slate-500 mt-1">
          Rule of thumb: <strong>3 ACH minimum</strong> for resin installs — the extractor must shift 3× the room's cubic metres every hour.
        </p>
      </div>
      <ResultTile
        label="Room volume"
        value={volume.toFixed(1)}
        unit="m³ (area × height)"
        tone="blue"
        testid="vent-volume"
      />
      <ResultTile
        label="Extractor rate needed"
        value={rate.toFixed(0)}
        unit={`m³/h  ·  ≈ ${cfm.toFixed(0)} CFM`}
        tone="purple"
        testid="vent-rate"
      />
      <div className="md:col-span-2 rounded-lg border-2 border-dashed border-slate-300 p-3 text-xs text-slate-600">
        <strong>How to spec:</strong> pick an extractor rated at <strong>≥ {rate ? rate.toFixed(0) : '—'} m³/h</strong>.
        If the room is enclosed with no cross-ventilation, bump the ACH to 5–6 and re-check.
      </div>
    </CalcShell>
  );
}

// ---------------------------------------------------------------------------
// Coming-soon placeholder — same modal shell for calcs the user hasn't
// specified formulas for yet.
// ---------------------------------------------------------------------------
export function ComingSoonCalc({ note }) {
  return (
    <div className="p-6 text-center">
      <p className="text-sm text-slate-500">{note || 'This calculator is coming soon.'}</p>
      <p className="text-xs text-slate-400 mt-2">
        Ping Dan the formula (m² or coverage per unit) and it'll be live within minutes.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Modal wrapper — a dialog for one calculator at a time.
// ---------------------------------------------------------------------------
export function CalculatorModal({ open, onClose, title, children }) {
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <div className="mt-2">{children}</div>
      </DialogContent>
    </Dialog>
  );
}
